import { Checkbox, Fieldset, ValidationMessage } from '@digdir/designsystemet-react';
import { useTranslation } from 'react-i18next';
import { FieldPathByValue, get } from 'react-hook-form';
import { FC } from 'react';

import { useOppvarmingFormContext } from '../../../utils';
import { OppvarmingBruk, OppvarmingFormData } from '../../../../types';

import { useVarmesystemValidation } from './useVarmesystemValidation';
import { useVentilasjonsData } from './useVentilasjonsData';

type VarmesystemBrukPath = FieldPathByValue<OppvarmingFormData, OppvarmingBruk>;

interface Props {
  name: VarmesystemBrukPath;
}

export const VarmesystemBrukCheckboxes: FC<Props> = ({ name }) => {
  const { register, setValue, formState, getValues, clearErrors } = useOppvarmingFormContext();
  const { validateVarmesystemBruk } = useVarmesystemValidation();
  const { hasBalansertVentilasjon } = useVentilasjonsData();
  const { t } = useTranslation('', { keyPrefix: 'form.oppvarming.common' });

  // This checkbox group has special behavior:
  // - If "Vet ikke" is selected, all other options are deselected.
  // - If any other option is selected, "vet ikke" is deselected.
  const onUkjentFordelingSelect = () => {
    setValue(`${name}.romoppvarming`, false);
    setValue(`${name}.tappevann`, false);
    setValue(`${name}.ventilasjonVarmebatteri`, false);

    // We display a warning on form submit when no oppvarmingskilde is selected for romoppvarming.
    // Clear romoppvarmingMissing error when making a selection.
    clearErrors('root.romoppvarmingMissing');
  };

  const onFordelingSelect = () => {
    setValue(`${name}.ukjent`, false);

    // We display a warning on form submit when no oppvarmingskilde is selected for romoppvarming.
    // Clear romoppvarmingMissing error when making a selection
    clearErrors('root.romoppvarmingMissing');
  };

  // All field share error message and state. We use "romoppvarming" error on all fields.
  const fieldError = get(formState.errors, `${name}.romoppvarming`, {});
  const errorMessage = fieldError?.message;

  const varmesystemBrukValues = getValues(name);

  return (
    <Fieldset>
      <Fieldset.Legend>{t('kjel.oppvarmingBruk.label')}</Fieldset.Legend>
      <Checkbox
        aria-invalid={!!errorMessage}
        label={t('kjel.oppvarmingBruk.options.romoppvarming')}
        {...register(`${name}.romoppvarming`, {
          onChange: onFordelingSelect,
          validate: () => validateVarmesystemBruk(varmesystemBrukValues),
          deps: [name],
          shouldUnregister: true
        })}
      />
      <Checkbox
        aria-invalid={!!errorMessage}
        label={t('kjel.oppvarmingBruk.options.tappevann')}
        {...register(`${name}.tappevann`, {
          onChange: onFordelingSelect,
          validate: () => validateVarmesystemBruk(varmesystemBrukValues),
          deps: [name],
          shouldUnregister: true
        })}
      />

      {/* Ventilasjon varmebatteri is only relevant in case of balansert ventilasjon */}
      {hasBalansertVentilasjon && (
        <Checkbox
          aria-invalid={!!errorMessage}
          label={t('kjel.oppvarmingBruk.options.ventilasjonVarmebatteri')}
          {...register(`${name}.ventilasjonVarmebatteri`, {
            onChange: onFordelingSelect,
            validate: () => validateVarmesystemBruk(varmesystemBrukValues),
            deps: [name],
            shouldUnregister: true
          })}
        />
      )}
      <Checkbox
        aria-invalid={!!errorMessage}
        label={t('kjel.oppvarmingBruk.options.vetIkke')}
        {...register(`${name}.ukjent`, {
          onChange: onUkjentFordelingSelect,
          validate: () => validateVarmesystemBruk(varmesystemBrukValues),
          deps: [name],
          shouldUnregister: true
        })}
      />
      {!!errorMessage && <ValidationMessage>{errorMessage}</ValidationMessage>}
    </Fieldset>
  );
};
